import React from 'react'
import { without } from 'underscore'

import styled from 'styled-components'

import { Button } from '@nexiahome/ui-common/components/primitives/buttons'
import ColumnGridPanel from 'components/ColumnGridPanel'

import ConfigDropdown from 'pages/Device/SystemConfigPanel/ConfigDropdown'

import { SystemConfiguration, ShutdownDevice } from 'types/SystemConfigReport'

import { friendlyName } from 'helpers/formatters'

import { color } from 'styles/colors'

export type Props = Pick<SystemConfiguration, 'shutdownDevices'> & {
  isEditing: boolean
  onChangeCallback: (arg: Partial<SystemConfiguration>) => void
}

const DEVICE_TYPES = {
  ah_coil_condensate_switch: 'AH Coil Condensate Switch',
  furnace_condensate_switch: 'Furnace Condensate Switch',
  smoke_detector: 'Smoke Detector',
  frost_stat_defrost_kit: 'Frost Stat Defrost Kit',
  load_shed_device: 'Load Shed Device',
  other: 'Other'
}

const WIRING_METHODS = {
  to_ah_input: 'To AH Input',
  breaks_y_to_odu: 'Breaks Y To ODU',
  breaks_r_to_stat: 'Breaks Y To Stat',
  other: 'Other'
}

const ShutDownDevices = ({ isEditing, onChangeCallback, shutdownDevices = [] }: Props) => {
  const deviceTypeOptions = [...Object.keys(DEVICE_TYPES).map(key => [{ value: key, name: friendlyName(key, DEVICE_TYPES) }])]
  const wiringMethodOptions = [...Object.keys(WIRING_METHODS).map(key => [{ value: key, name: friendlyName(key, WIRING_METHODS) }])]

  const updateShutdownDevices = (updatedDevices) => {
    onChangeCallback({ shutdownDevices: updatedDevices })
  }

  const onChangeDeviceType = (device: ShutdownDevice, value: string | null) => {
    device.deviceType = value
    updateShutdownDevices(shutdownDevices)
  }

  const onChangeWiringMethod = (device: ShutdownDevice, value: string | null) => {
    device.wiringMethod = value
    updateShutdownDevices(shutdownDevices)
  }

  const addShutDownDevice = () => {
    const newDevice: ShutdownDevice = {
      deviceType: null,
      wiringMethod: null
    }

    updateShutdownDevices([...shutdownDevices, newDevice])
  }

  const onDeleteShutdownDevice = (device: ShutdownDevice) => {
    if (confirm('Are you sure you want to remove this shutdown device? This cannot be undone.')) {
      updateShutdownDevices(without(shutdownDevices, device))
    }
  }

  return (
    <>
      {shutdownDevices?.map((device, index) =>
        (<S.ColumnItem
          isEditing={isEditing}
          key={`${device.deviceType}_${device.wiringMethod}_${index.toString()}`}
          label={
            isEditing
              ? (
                <S.Container>
                  <S.DeleteIcon className="icon-remove" onClick={() => onDeleteShutdownDevice(shutdownDevices[index])} aria-label="remove shutdown device" role="button" tabIndex={0} />
                  <S.ShutdownDeviceType isEditing autoFocus options={deviceTypeOptions} value={device.deviceType} onChangeCallback={(value) => onChangeDeviceType(shutdownDevices[index], value as string)} />
                </S.Container>
              )
              : <S.ShutdownDeviceType isEditing={false} options={deviceTypeOptions} value={device.deviceType} onChangeCallback={(value) => onChangeDeviceType(shutdownDevices[index], value as string)} />
          }
          value={<ConfigDropdown isEditing={isEditing} options={wiringMethodOptions} value={device.wiringMethod} onChangeCallback={(value) => onChangeWiringMethod(shutdownDevices[index], value as string)} />}
         />
        ))
      }
      {isEditing && (
        <Button decoration="textOnly" text="Add Shutdown Device" icon="new" onPress={addShutDownDevice} />
      )}
    </>
  )
}

export default ShutDownDevices

const S = {
  DeleteIcon: styled.i`
    font-size: 14px;
    color: ${color('deleteIcon')};
    padding: 7px;
    margin-left: -7px;
    cursor: pointer;
  `,
  Container: styled.div.withConfig({
    shouldForwardProp: (prop) => !['isEditing'].includes(prop)
  })<{ isEditing?: boolean }>`
    display: inline-block;
    align-items: center;
    ${({ isEditing }) => isEditing && 'margin-left: -5px;'}
  `,
  ColumnItem: styled(ColumnGridPanel.Column.Item).withConfig({
    shouldForwardProp: (prop) => !['isEditing'].includes(prop)
  })<{ isEditing: boolean }>`
    label {
      ${({ isEditing }) => isEditing && 'overflow: unset;'}
    }
  `,
  ShutdownDeviceType: styled(ConfigDropdown)`
    display: inline-block;
    width: calc(100% - 22px);
  `
}
